import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Menu, ShoppingBag, KeyRound, BedDouble, Tag, LogIn } from "lucide-react";
import { PC_MARK } from "@/lib/assets";
import { BrandLogo } from "@/components/BrandLogo";

const PUBLIC_LINKS = [
  { key: "buy", label: "Buy", to: "/marketplace?type=For%20Sale", icon: ShoppingBag },
  { key: "rent", label: "Rent", to: "/marketplace?type=For%20Rent", icon: KeyRound },
  { key: "stays", label: "Stays", to: "/marketplace?type=Unit%20Hosting", icon: BedDouble },
  { key: "sell", label: "Sell", to: "/sell", icon: Tag },
];

export function PublicHeader() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <header data-testid="public-header" className="sticky top-0 z-40 bg-white border-b border-slate-200">
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 h-[72px] sm:h-[96px] flex items-center justify-between gap-2 sm:gap-4">
        <Link to="/marketplace" aria-label="PropertyConnect marketplace" className="flex items-center gap-2 sm:gap-2.5 shrink-0 min-w-0" data-testid="public-logo-link">
          <BrandLogo className="h-[64px] sm:h-[88px]" />
        </Link>

        {/* Desktop / tablet ≥768px: full inline navigation */}
        <nav className="hidden md:flex items-center gap-0.5 lg:gap-2 min-w-0">
          {PUBLIC_LINKS.map((l) => (
            <button
              key={l.key}
              data-testid={`nav-${l.key}`}
              onClick={() => nav(l.to)}
              className="px-2 lg:px-3 py-2 text-[13px] lg:text-sm font-semibold text-slate-700 hover:text-[#0F2C59] transition-colors"
            >
              {l.label}
            </button>
          ))}
          <Link to="/login" data-testid="signin-link" className="shrink-0">
            <Button className="bg-[#0F2C59] hover:bg-[#0F2C59]/90 text-white rounded-xl font-semibold h-9 px-4 text-sm">Sign in</Button>
          </Link>
        </nav>

        {/* Mobile <768px: hamburger menu (existing Sheet pattern) */}
        <div className="flex items-center gap-2 md:hidden">
          <Link to="/login" data-testid="signin-link-mobile" className="shrink-0">
            <Button className="bg-[#0F2C59] hover:bg-[#0F2C59]/90 text-white rounded-xl font-semibold h-8 px-3 text-xs">Sign in</Button>
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger data-testid="public-mobile-trigger" aria-label="Open menu" className="h-10 w-10 rounded-full hover:bg-slate-100 flex items-center justify-center shrink-0"><Menu className="h-5 w-5 text-[#0F2C59]" /></SheetTrigger>
            <SheetContent side="right" className="w-80 p-0 overflow-y-auto" data-testid="public-mobile-nav">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <SheetDescription className="sr-only">PropertyConnect public navigation</SheetDescription>
              <div className="p-4 space-y-1">
                {PUBLIC_LINKS.map((l) => (
                  <button
                    key={l.key}
                    data-testid={`mnav-${l.key}`}
                    onClick={() => { nav(l.to); setOpen(false); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[15px] font-medium text-slate-700 hover:bg-slate-100"
                  >
                    <l.icon className="h-4 w-4 text-slate-500" /> {l.label}
                  </button>
                ))}
                <div className="h-px bg-slate-200 my-2" />
                <button
                  data-testid="mnav-signin"
                  onClick={() => { nav("/login"); setOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[15px] font-semibold text-[#0F2C59] hover:bg-slate-100"
                >
                  <LogIn className="h-4 w-4" /> Sign in
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
